'use client'
import { isErrorReported } from '@repo/observability/errors'
import { Button } from '@repo/ui'
import { QueryErrorResetBoundary } from '@tanstack/react-query'
import React, { type ReactNode } from 'react'

import InnerLayout from '@/components/layout/InnerLayout/InnerLayout'
import { logger } from '@/lib/observability/logger'

import { SectionErrorBoundaryResetContext } from './SectionErrorBoundary.context'

interface SectionErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
  route?: string
  section?: string
  title?: string
  description?: string
}

interface SectionErrorBoundaryInnerProps extends SectionErrorBoundaryProps {
  onReset: () => void
}

interface SectionErrorBoundaryState {
  hasError: boolean
}

class SectionErrorBoundaryInner extends React.Component<
  SectionErrorBoundaryInnerProps,
  SectionErrorBoundaryState
> {
  constructor(props: SectionErrorBoundaryInnerProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(): SectionErrorBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: Error) {
    if (isErrorReported(error)) return

    logger.fatal(error, {
      route: this.props.route,
      tags: { section: this.props.section ?? 'unknown' }
    })
  }

  reset = () => {
    this.props.onReset()
    this.setState({ hasError: false })
  }

  render() {
    if (this.state.hasError) {
      let fallback = this.props.fallback

      if (fallback === undefined) {
        fallback = (
          <InnerLayout>
            <div className="gap-lg flex flex-col items-center justify-center p-4">
              <span className="text-title-lg text-foreground">
                {this.props.title ?? 'Section Error'}
              </span>
              <span className="text-foreground text-lg">
                {this.props.description ?? `Something went wrong in ${this.props.section}.`}
              </span>
              <Button onClick={this.reset} size="md">
                Try again
              </Button>
            </div>
          </InnerLayout>
        )
      }

      return (
        <SectionErrorBoundaryResetContext.Provider value={this.reset}>
          {fallback}
        </SectionErrorBoundaryResetContext.Provider>
      )
    }

    return this.props.children
  }
}

export default function SectionErrorBoundary(props: SectionErrorBoundaryProps) {
  return (
    <QueryErrorResetBoundary>
      {({ reset }) => <SectionErrorBoundaryInner {...props} onReset={reset} />}
    </QueryErrorResetBoundary>
  )
}
